import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { addEnquiry, businessInfo } from '../mock.js';
import { useToast } from '../hooks/use-toast.js';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    machineModel: '',
    partRequirement: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    addEnquiry({
      type: 'contact',
      ...formData
    });
    toast({
      title: "Enquiry Submitted!",
      description: "We'll get back to you shortly with availability and pricing.",
    });
    setFormData({
      name: '',
      phone: '',
      email: '',
      city: '',
      machineModel: '',
      partRequirement: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-gradient-to-br from-gray-50 to-gray-100 py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Get In <span className="text-[#FF6B7A]">Touch</span>
            </h1>
            <p className="text-lg text-gray-700">
              Send us your requirement and we'll confirm availability and pricing
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
              </div>

              <Card className="border-2">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-3 mb-4">
                    <MapPin className="h-6 w-6 text-[#00C9FF] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
                      <p className="text-sm text-gray-600">{businessInfo.address}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 mb-4">
                    <Phone className="h-6 w-6 text-[#00C9FF] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                      <a href={`tel:${businessInfo.phone}`} className="text-sm text-[#FF6B7A] hover:underline">
                        {businessInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 mb-4">
                    <MessageCircle className="h-6 w-6 text-[#25D366] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">WhatsApp</h3>
                      <a 
                        href={`https://wa.me/${businessInfo.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[#25D366] hover:underline"
                      >
                        {businessInfo.whatsapp}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 mb-4">
                    <Mail className="h-6 w-6 text-[#00C9FF] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                      <a href={`mailto:${businessInfo.email}`} className="text-sm text-[#FF6B7A] hover:underline">
                        {businessInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Clock className="h-6 w-6 text-[#00C9FF] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Business Hours</h3>
                      <p className="text-sm text-gray-600">{businessInfo.businessHours}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 bg-gradient-to-br from-[#FF6B7A]/5 to-[#00C9FF]/5">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">GST Details</h3>
                  <p className="text-sm text-gray-600 mb-1">Registered Business</p>
                  <p className="text-sm font-mono text-gray-900">{businessInfo.gst}</p>
                </CardContent>
              </Card>
            </div>

            {/* Enquiry Form */}
            <div className="lg:col-span-2">
              <Card className="border-2">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Send Enquiry</h2>
                  <p className="text-gray-600 mb-6">Fill in your details and part requirement</p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-base">Your Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter your name"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-base">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="10-digit mobile number"
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email" className="text-base">Email Address</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="city" className="text-base">City *</Label>
                        <Input
                          id="city"
                          name="city"
                          type="text"
                          required
                          value={formData.city}
                          onChange={handleChange}
                          placeholder="Your city"
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="machineModel" className="text-base">Machine Model *</Label>
                      <Input
                        id="machineModel"
                        name="machineModel"
                        type="text"
                        required
                        value={formData.machineModel}
                        onChange={handleChange}
                        placeholder="e.g., JCB 3DX, Forklift 3-ton diesel"
                        className="mt-2"
                      />
                      <p className="text-xs text-gray-500 mt-1">Specify your JCB model or forklift type</p>
                    </div>

                    <div>
                      <Label htmlFor="partRequirement" className="text-base">Part Requirement *</Label>
                      <Textarea
                        id="partRequirement"
                        name="partRequirement"
                        required
                        value={formData.partRequirement}
                        onChange={handleChange}
                        placeholder="Describe the spare part you need (e.g., hydraulic pump, engine piston, brake pads)..."
                        className="mt-2 min-h-32"
                      />
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button 
                        type="submit"
                        size="lg"
                        className="flex-1 bg-[#FF6B7A] hover:bg-[#FF5A6A] text-white"
                      >
                        <Send className="mr-2 h-5 w-5" />
                        Submit Enquiry
                      </Button>
                      <Button 
                        type="button"
                        size="lg"
                        onClick={() => {
                          const message = encodeURIComponent("Hi, I need information about spare parts.");
                          window.open(`https://wa.me/${businessInfo.whatsapp}?text=${message}`, '_blank');
                        }}
                        className="bg-[#25D366] hover:bg-[#20BD5A] text-white"
                      >
                        <MessageCircle className="mr-2 h-5 w-5" />
                        WhatsApp
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Visit Our Location</h2>
            <Card className="border-2 overflow-hidden">
              <CardContent className="p-0">
                <div className="aspect-video bg-gray-200 flex items-center justify-center">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3721.5!2d79.088!3d21.145!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjHCsDA4JzQyLjAiTiA3OcKwMDUnMTYuOCJF!5e0!3m2!1sen!2sin!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Sparecraft Solutions Location"
                  ></iframe>
                </div>
                <div className="p-6 bg-white">
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-6 w-6 text-[#00C9FF] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Sparecraft Solutions</h3>
                      <p className="text-sm text-gray-600">{businessInfo.address}</p>
                      <p className="text-sm text-gray-500 mt-2">{businessInfo.region}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
